import { useState, useEffect, useCallback } from 'react';

// Hook to detect online/offline status
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

// Hook to handle hourly sync from main thread instead of service worker
export const useHourlySync = (syncFunction, intervalMinutes = 60) => {
  const [isActive, setIsActive] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    let interval;

    if (isActive && syncFunction) {
      // Set up interval without running immediately
      interval = setInterval(() => {
        console.log(`Running sync every ${intervalMinutes} minutes`);
        syncFunction();
        setLastSync(new Date());
      }, intervalMinutes * 60 * 1000); // Convert minutes to milliseconds

      console.log(`Hourly sync started - running every ${intervalMinutes} minutes`);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
        console.log('Hourly sync stopped');
      }
    };
  }, [isActive, intervalMinutes, syncFunction]); // Added syncFunction back since it's now useCallback wrapped

  const startSync = useCallback(() => setIsActive(true), []);
  const stopSync = useCallback(() => setIsActive(false), []);

  return {
    isActive,
    lastSync,
    startSync,
    stopSync
  };
};

// Hook to interact with service worker
export const useServiceWorker = () => {
  const [swRegistration, setSwRegistration] = useState(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        setSwRegistration(registration);

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setIsUpdateAvailable(true);
              }
            });
          }
        });
      });
    }
  }, []);

  const updateApp = () => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  const cacheSize = async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      let totalSize = 0;
      
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        totalSize += keys.length;
      }
      
      return totalSize;
    }
    return 0;
  };

  const clearCache = async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('All caches cleared');
    }
  };

  return {
    registration: swRegistration,
    isUpdateAvailable,
    updateApp,
    cacheSize,
    clearCache
  };
};
